import React, { memo, useMemo, useState } from "react";
import styled from "styled-components";
import { theme } from "uikit";

import { Popup } from "../common";
import { Language } from "../../assets/languages/langs";

import SignalIcon, { INACTIVE_ON_LIGHT } from "./SignalIcon";
import ConnectionDetails from "./Details";
import ConnectionErrorBoundary from "./ConnectionErrorBoundary";
import useConnectionStats from "./useConnectionStats";
import useConnectionText from "./useConnectionText";
import { hasConnectionHint, isNoLink } from "./constants";

const Line = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;

	font-family: Lato;
	font-size: 13px;
	line-height: 16px;

	color: ${theme.colors.secondary};

	cursor: default;
`;

const Hint = styled.div<{ alert: boolean }>`
	max-width: 260px;
	margin-top: 6px;

	font-family: Lato;
	font-size: 12px;
	line-height: 16px;

	color: ${({ alert }) => (alert ? theme.colors.error : "#5e6b73")};
`;

/** Icon and state; reads the store itself so pings do not touch Popup. */
const StatusLine = memo(({ lang }: { lang: Language }) => {
	const t = useConnectionText(lang);
	const { level } = useConnectionStats();

	return (
		<div>
			<Line>
				<SignalIcon level={level} inactiveColor={INACTIVE_ON_LIGHT} />
				<span>
					{t("title")}: {t(`level.${level}`)}
				</span>
			</Line>
			{hasConnectionHint(level) && (
				<Hint alert={isNoLink(level)}>{t(`hint.${level}`)}</Hint>
			)}
		</div>
	);
});

const popupOffset = { x: 0, y: 28 };

/**
 * Status line for the login screen: icon, state, advice, details on hover.
 * Texts from the built-in dictionary in `lang`: before login the server
 * translations are not available.
 */
const ConnectionStatus = memo(({ lang }: ConnectionStatus.Props) => {
	const [open, setOpen] = useState(false);

	// Same elements on every render: opening must not change the content.
	// Each part has its own guard: the panel renders in the root float
	// container, outside this tree.
	const tracker = useMemo(
		() => (
			<ConnectionErrorBoundary>
				<StatusLine lang={lang} />
			</ConnectionErrorBoundary>
		),
		[lang],
	);
	const details = useMemo(
		() => (
			<ConnectionErrorBoundary>
				<ConnectionDetails lang={lang} />
			</ConnectionErrorBoundary>
		),
		[lang],
	);

	return (
		<Popup
			useHoverControl
			open={open}
			offset={popupOffset}
			tracker={tracker}
			onChangeOpen={setOpen}
		>
			{details}
		</Popup>
	);
});

declare namespace ConnectionStatus {
	interface Props {
		lang: Language;
	}
}

export default ConnectionStatus;
